/* EN-4396: the level (and division filter) the orders pages read right now.

   The service layer and the query keys are plain functions, not hooks, so they
   read this module's state. useActiveOrdersScope() (called once by the list
   and the detail page) moves it to what useAccountView() says; a change of
   level or filter drops every cached orders query, so the other level's rows
   are never shown. */
import { useEffect, useSyncExternalStore } from 'react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { orderKeys } from '@features/orders/lib/query-keys';
import { joinList, parseList } from '@features/orders/lib/filters';
import {
  DIVISION_FILTER_PARAM,
  type DivisionRef,
  type OrdersScopeLevel,
  scopeParams,
} from './scope';
import { useAccountView } from './useAccountView';

export type ActiveScope = {
  level: OrdersScopeLevel;
  /** The account's divisions; empty at the division level. */
  divisions: readonly DivisionRef[];
  /** Ids the list is narrowed to; empty means every division. */
  divisionFilter: readonly string[];
};

/** What a row or detail without a division (a connection with none) shows. */
export const UNASSIGNED_LABEL = 'Unassigned';

const NONE: readonly never[] = [];
/** The empty division list: pass this one, not a fresh [], to followAccountView,
    which compares lists by identity (two callers in one render must agree). */
export const NO_DIVISIONS: readonly DivisionRef[] = NONE;
const DIVISION_LEVEL: ActiveScope = {
  level: 'division',
  divisions: NONE,
  divisionFilter: NONE,
};

let state: ActiveScope = DIVISION_LEVEL;
/* The cache still holds what `state` replaced: cleared once a render is over. */
let isDirty = false;
let cache: QueryClient | undefined;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getState = () => state;

/** Query params of the active level for the service layer; empty at division. */
export const activeScopeParams = (): Record<string, string> =>
  scopeParams(state.level, state.divisionFilter);

/** For query keys: the cache holds each level and filter apart. */
export const activeScopeKey = () => ({
  level: state.level,
  divisionFilter: state.divisionFilter,
});

/* Read when called, not on load: query-keys imports this module too. */
function dropCachedOrders(client: QueryClient) {
  /* The client-only entries (EN-4188) cannot be read again, so they stay. */
  const kept = orderKeys.pendingLabels('')[1];

  client.invalidateQueries({ queryKey: orderKeys.all, refetchType: 'none' });
  client.removeQueries({
    queryKey: orderKeys.all,
    predicate: query => query.queryKey[1] !== kept,
  });
}

function notify() {
  for (const listener of [...listeners]) listener();
}

const sameList = (a: readonly string[], b: readonly string[]) =>
  joinList([...a]) === joinList([...b]);

/* Moves the state; the cache is dropped by the next settle(). */
function moveTo(next: ActiveScope) {
  const changed =
    next.level !== state.level ||
    !sameList(next.divisionFilter, state.divisionFilter);
  isDirty = isDirty || changed;
  state = next;
}

/**
 * Follows the account view during a render, so the same render already keys
 * and sends its queries for the new level. A new level starts from the
 * filter in the address bar (`urlFilter`), kept to divisions that exist.
 */
export function followAccountView(
  level: OrdersScopeLevel,
  divisions: readonly DivisionRef[],
  urlFilter: string | null,
) {
  if (level === state.level && divisions === state.divisions) return;
  const known = new Set(divisions.map(division => division.id));
  const fromUrl =
    level === 'account'
      ? parseList(urlFilter ?? undefined).filter(id => known.has(id))
      : NONE;
  moveTo({
    level,
    divisions,
    divisionFilter: level === state.level ? state.divisionFilter : fromUrl,
  });
}

/** After the render: drops the replaced level's cache, then tells readers. */
export function settleActiveScope(client?: QueryClient) {
  cache = client ?? cache;
  if (!isDirty) return;
  isDirty = false;
  if (cache) dropCachedOrders(cache);
  notify();
}

export function setDivisionFilter(ids: readonly string[]) {
  moveTo({ ...state, divisionFilter: ids });
  settleActiveScope();
}

/** Forgets the level, as a sign-out or a test needs. */
export function resetActiveScope() {
  state = DIVISION_LEVEL;
  isDirty = false;
  notify();
}

/** Reads the active scope; a component below the page uses this one. */
export function useActiveScope() {
  return {
    ...useSyncExternalStore(subscribe, getState, getState),
    setDivisionFilter,
  };
}

/** Called once by the list page and the detail page. */
export function useActiveOrdersScope() {
  const { view, level } = useAccountView();
  const client = useQueryClient();
  const [searchParams] = useSearchParams();

  followAccountView(
    level,
    view.available ? view.divisions : NO_DIVISIONS,
    searchParams.get(DIVISION_FILTER_PARAM),
  );
  useEffect(() => settleActiveScope(client));

  return useActiveScope();
}

type WithDivision = {
  division_id?: string | null;
  division_name?: string | null;
};

/** The division a row or detail belongs to, or `Unassigned`. */
export const divisionLabelOf = (row: WithDivision): string =>
  row.division_name ?? UNASSIGNED_LABEL;

/** Account-level rows carry the key (null when unassigned); division-level rows do not. */
export const hasDivision = (row: unknown): row is WithDivision =>
  typeof row === 'object' && row !== null && 'division_id' in row;
